import { useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';

import { BootstrapSignalKeys, BootstrapSignals } from '@/entities/tracking/lib/bootstrap-signals';
import { appLogger } from '@/utils/app-logger/appLogger';
import {
    APP_LAUNCH_API_PATH,
    APP_LAUNCH_CHECK_LOCK,
    APP_LAUNCHED_TAB_KEY,
    APP_OPEN_LOCK,
} from '@/utils/constants/common';

const COMPONENT = 'useTrackAppLaunchCount';

const reportAppLaunch = async (newLaunch: boolean): Promise<void> => {
    const response = await fetch(APP_LAUNCH_API_PATH, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newLaunch }),
    });
    if (!response.ok) {
        throw new Error(`App launch request failed with status ${response.status}`);
    }
};

const holdAppOpenLock = (locks: LockManager): void => {
    locks
        .request(APP_OPEN_LOCK, { mode: 'shared' }, () => new Promise<never>(() => {}))
        .catch((error: unknown) => {
            appLogger.error('Failed to hold the app open lock', { component: COMPONENT }, error);
        });
};

// Every open app tab holds a shared lock until it closes, so a tab that finds it free, and was not
// itself already launched (reload, restored tab), is the first since the app was last closed.
// Without Web Locks this reports false and the server falls back to once per browser session.
const detectNewLaunch = async (): Promise<boolean> => {
    const { locks } = navigator;
    if (!locks) return false;
    try {
        const tabAlreadyLaunched = sessionStorage.getItem(APP_LAUNCHED_TAB_KEY) !== null;
        sessionStorage.setItem(APP_LAUNCHED_TAB_KEY, '1');
        // Tabs check and claim one at a time, so two tabs loading together cannot both find the app
        // open lock free. A claim the browser has not granted yet shows up as pending.
        const otherTabOpen = await locks.request(APP_LAUNCH_CHECK_LOCK, async () => {
            const { held = [], pending = [] } = await locks.query();
            holdAppOpenLock(locks);
            return [...held, ...pending].some(({ name }) => name === APP_OPEN_LOCK);
        });
        return !tabAlreadyLaunched && !otherTabOpen;
    } catch (error) {
        appLogger.error('Failed to detect a new app launch', { component: COMPONENT }, error);
        return false;
    }
};

/**
 * Reports an app launch once the app is ready for input (LAUNCH_READY), flagging it as new when no
 * other tab of the app is open. The server also applies a once-per-browser-session rule, so this
 * fires on every full page load.
 */
export function useTrackAppLaunchCount(): void {
    const launchDetection = useRef<Promise<boolean> | null>(null);
    const { mutate } = useMutation({
        mutationFn: reportAppLaunch,
        gcTime: 0,
        onError: (error) => {
            appLogger.error(
                'Failed to record app launch count',
                { component: COMPONENT, endpoint: APP_LAUNCH_API_PATH },
                error,
            );
        },
    });

    useEffect(() => {
        let cancelled = false;
        // Detection claims the tab, so it must run once per page load even if the effect re-runs.
        launchDetection.current ??= detectNewLaunch();
        // A timed-out wait still counts: LAUNCH_READY itself is marked on timeout.
        Promise.all([
            launchDetection.current,
            BootstrapSignals.whenAll([BootstrapSignalKeys.LAUNCH_READY]),
        ]).then(([newLaunch]) => {
            if (!cancelled) mutate(newLaunch);
        });
        return () => {
            cancelled = true;
        };
    }, [mutate]);
}
